import { AxiosResponse } from 'axios';
import api from './api';

export interface FeatureFlag {
  id: string;
  key: string;
  name: string;
  description?: string;
  isEnabled: boolean;
  config?: any;
  category?: string;
  version: string;
  createdAt: string;
  updatedAt: string;
  enabledAt?: string;
  disabledAt?: string;
  organizationId: string;
}

export interface CreateFeatureFlagRequest {
  key: string;
  name: string;
  description?: string;
  isEnabled?: boolean;
  config?: any;
  category?: string;
  version?: string;
}

export interface UpdateFeatureFlagRequest {
  key?: string;
  name?: string;
  description?: string;
  isEnabled?: boolean;
  config?: any;
  category?: string;
  version?: string;
}

export interface ToggleFeatureFlagRequest {
  isEnabled: boolean;
}

export const featureFlagsApi = {
  // Get all feature flags for the organization
  getAll: (): Promise<AxiosResponse<FeatureFlag[]>> => {
    return api.get('/feature-flags');
  },

  // Get enabled feature keys
  getEnabled: (): Promise<AxiosResponse<string[]>> => {
    return api.get('/feature-flags/enabled');
  },

  // Check if a specific feature is enabled
  checkFeature: (key: string): Promise<AxiosResponse<{ key: string; isEnabled: boolean }>> => {
    return api.get(`/feature-flags/check/${key}`);
  },

  // Get feature flag by ID
  getById: (id: string): Promise<AxiosResponse<FeatureFlag>> => {
    return api.get(`/feature-flags/${id}`);
  },

  // Get feature flag by key
  getByKey: (key: string): Promise<AxiosResponse<FeatureFlag>> => {
    return api.get(`/feature-flags/key/${key}`);
  },

  // Create new feature flag
  create: (data: CreateFeatureFlagRequest): Promise<AxiosResponse<FeatureFlag>> => {
    return api.post('/feature-flags', data);
  },

  // Update feature flag
  update: (id: string, data: UpdateFeatureFlagRequest): Promise<AxiosResponse<FeatureFlag>> => {
    return api.patch(`/feature-flags/${id}`, data);
  },

  // Toggle feature flag by ID
  toggle: (id: string, isEnabled: boolean): Promise<AxiosResponse<FeatureFlag>> => {
    return api.put(`/feature-flags/${id}/toggle`, { isEnabled });
  },

  // Toggle feature flag by key
  toggleByKey: (key: string, isEnabled: boolean): Promise<AxiosResponse<FeatureFlag>> => {
    return api.put(`/feature-flags/key/${key}/toggle`, { isEnabled });
  },

  // Delete feature flag
  delete: (id: string): Promise<AxiosResponse<void>> => {
    return api.delete(`/feature-flags/${id}`);
  },

  // Initialize default feature flags
  initializeDefaults: (): Promise<AxiosResponse<void>> => {
    return api.post('/feature-flags/initialize');
  },
};